import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
import {AccountIdentity, AccountIdentityFilter} from '../_models/account-identity';
// Registers the String.prototype.dictReplace extension the reset URL uses (main.ts does this in the app).
import '../../../_services/string-extensions';

/**
 * The platform-owner **account administration** endpoints (#279 completing #282): the installation-wide
 * account directory, and the audited second-factor reset performed from it.
 *
 * Both are `PlatformOwner`-only on the server and read no tenant — they are platform surfaces, like
 * `/auth/account/admin/external-identities` beside them. This service is therefore only ever reached from the
 * owner shell; nothing company-scoped may call it.
 */
@Injectable({
  providedIn: 'root'
})
export class AccountAdminHttpService {
  constructor(@Inject('BASE_URL') private baseUrl: string, private httpClient: HttpClient) {
  }

  /**
   * The accounts, narrowed server-side.
   *
   * **A blank filter is omitted from the request entirely**, never sent as an empty value. `email=` is not the
   * same request as no `email` at all: the server reads a present-but-blank parameter as "do not narrow" today,
   * but that is its rule to change, and a URL that states a filter it does not mean is the kind of thing a
   * later `string.IsNullOrWhiteSpace` → `!= null` edit turns into "no results, and nobody knows why". The
   * omission also keeps the URL readable in a network log, which is where this screen gets debugged.
   *
   * `twoFactor` is compared against the two booleans explicitly rather than tested for truthiness — `false` is
   * a real filter here ("show me the accounts that have none"), and `if (filter.twoFactor)` would silently
   * drop exactly that half.
   */
  list(filter: AccountIdentityFilter = {}): Observable<AccountIdentity[]> {
    let params = new HttpParams();

    const provider = filter.provider?.trim();
    if (provider) params = params.set('provider', provider);

    const email = filter.email?.trim();
    if (email) params = params.set('email', email);

    const name = filter.name?.trim();
    if (name) params = params.set('name', name);

    if (filter.twoFactor === true || filter.twoFactor === false) {
      params = params.set('twoFactor', filter.twoFactor);
    }

    return this.httpClient.get<AccountIdentity[]>(
      `${this.baseUrl}${ServerEndpoints.Auth_Account_Admin_Identities}`, {params});
  }

  /**
   * Removes one account's second factor — the authenticator secret and every recovery code.
   *
   * POST with an empty body: the action carries no code, which is the whole point of it (the person it serves
   * can produce neither a TOTP code nor a recovery code), so the only argument is which account.
   */
  resetSecondFactor(identityId: string): Observable<void> {
    return this.httpClient.post<void>(
      `${this.baseUrl}${ServerEndpoints.Auth_Account_Admin_Identity_TwoFactorReset_$identityId$
        .dictReplace({identityId})}`, {});
  }
}
